import './index.css'

import chunk from 'lodash/chunk'
import intersection from 'lodash/intersection'
import * as React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Link,
  NavigateFunction,
  useNavigate,
  useParams,
} from 'react-router-dom'
import validation from 'react-validation-mixin'
import strategy from 'react-validatorjs-strategy'

import mixpanel from '@disco/api/mixpanel'
import type { TFunction } from '@disco/i18n/use-translation'
import { useTranslation } from '@disco/i18n/use-translation'
import { addContact } from '@disco/state-management/actions/contact-actions'
import {
  getContactsSavingStatus,
  getProDropdownOptions,
  getPublisherDropdownOptions,
} from '@disco/state-management/selectors/contacts'
import { getContactCustomFields } from '@disco/state-management/selectors/user'
import { areTagsIncludeValues } from '@disco/utils/tags'

import type { Contact, CustomContactField } from 'types/list'
import type { DropdownOption } from 'types/ui'
import type { Pro, Publisher } from 'types/writer'

import TagSelector from 'components/common/tag-selector'
import { DiscoModal } from 'components/ui'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import Checkbox from 'components/ui/checkbox'
import CustomDropdown from 'components/ui/dropdowns/custom-dropdown'
import Heading from 'components/ui/heading'
import Label from 'components/ui/label'
import TextField from 'components/ui/text-field'
import { doubleScrollModalStyles } from 'components/utils/modalStyles'

import IdentifierTextField from 'features/rights-data/components/identifier-text-field'
import {
  buildExternalIdentifiers,
  getIPIValidationError,
  getISNIValidationError,
  padIPI,
} from 'features/rights-data/utils/identifier-validation'

const disabledTags = ['business']

const proPromptTextCreator = label => `Create new PRO "${label}"`

const publisherPromptTextCreator = label => `Create new publisher "${label}"`

const optionalEmailTypes = ['Artist', 'Writer']

const getOptionalEmailValidation = (
  email: string,
  tags: string[],
  listId: string,
) => {
  if (!listId && intersection(optionalEmailTypes, tags).length > 0) return null
  else if (email && email.trim().length > 0) return null
  else return 'Please enter an email'
}

type Props = {
  validate: (...args: Array<any>) => any
  getValidationMessages: (...args: Array<any>) => any
  navigate: NavigateFunction
  addContact: typeof addContact
  proDropdownOptions: DropdownOption[]
  publisherDropdownOptions: DropdownOption[]
  saving: boolean
  customFieldRows: CustomContactField[][]
  params: {
    listId: string
  }
  t: TFunction
}
type OwnState = {
  name: string
  email: string
  consent: boolean
  tags: string[]
  phone: string
  company: string
  territory: string
  publishers: Publisher[]
  acts: string
  pros: Pro[]
  ipi: string
  isni: string
  ipiError: string | null
  isniError: string | null
  optionalEmailError: string | null | undefined
  custom_data: Record<string, any>
}

class ContactAdd extends React.Component<Props, OwnState> {
  consentCheckboxVisible() {
    return !!this.props.params.listId
  }

  getValidatorData() {
    return {
      name: this.state.name,
      email: this.state.email,
      consent: this.state.consent || !this.consentCheckboxVisible(),
    }
  }

  validatorTypes = strategy.createSchema(
    {
      name: 'required',
      email: 'email',
      consent: 'accepted',
    },
    {
      'required.name': 'Please enter a name',
      'email.email': 'Please enter an email',
      'accepted.consent':
        'Sorry, we can’t import recipients you don’t have permission to email. ',
    },
    /*
  validator => {
    // console.log('validator call')
    const emailIsRequired = !optionalEmailTypes.includes(this.state.type)
    validator.constructor.register(
      'email_requirement',
      (value, attr, req) => {
        // console.log('optionalEmailRequirement', value, emailIsRequired)
        if (!emailIsRequired) return true
        return value && value.trim().length > 0
      },
      'Please enter an email (optinal validation)',
    )
  },
  */
  )
  state: OwnState = {
    name: '',
    tags: [],
    email: '',
    phone: '',
    company: '',
    territory: '',
    publishers: [],
    acts: '',
    pros: [],
    ipi: '',
    isni: '',
    ipiError: null,
    isniError: null,
    consent: false,
    optionalEmailError: null,
    custom_data: {},
  }
  _onClose = () => {
    const { listId } = this.props.params
    this.props.navigate(listId ? `/lists/${listId}` : '/contacts/list')
  }
  _showIdentifierFields = (tags: string[] = this.state.tags) =>
    tags.includes('Writer')
  _onSubmit = () => {
    const { listId } = this.props.params
    const optionalEmailError = getOptionalEmailValidation(
      this.state.email,
      this.state.tags,
      listId,
    )
    const showIdentifierFields = this._showIdentifierFields()
    const ipiError = showIdentifierFields
      ? getIPIValidationError(this.state.ipi, this.props.t)
      : null
    const isniError = showIdentifierFields
      ? getISNIValidationError(this.state.isni, this.props.t)
      : null
    this.setState({
      optionalEmailError,
      ipiError,
      isniError,
    })
    if (!optionalEmailError && !ipiError && !isniError) {
      this.props.validate(this._onValidate)
    }
  }
  _onValidate = async error => {
    if (error) return
    const {
      name,
      email,
      phone,
      company,
      territory,
      publishers,
      acts,
      pros,
      ipi,
      isni,
      tags,
      custom_data,
    } = this.state

    if (!name) {
      return
    }

    const showIdentifierFields = this._showIdentifierFields(tags)
    const external_identifiers = buildExternalIdentifiers(
      showIdentifierFields ? padIPI(ipi) : undefined,
      showIdentifierFields ? isni : undefined,
    )

    const contact = {
      pendingChanges: {
        name,
        email,
        tags,
        phone,
        company,
        territory,
        publishers,
        acts,
        pros,
        custom_data,
        ...(external_identifiers !== undefined && { external_identifiers }),
      },
    }
    let listId

    if (this.props.params.listId) {
      listId = parseInt(this.props.params.listId, 10)
    }

    await this.props.addContact(contact as Contact, listId)
    mixpanel.sendPureEvent('Add contact (single)')

    this._onClose()
  }
  _handleConsent = (label, value) => {
    this.setState({
      consent: value,
    })
  }
  _onFieldChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    // @ts-expect-error
    this.setState({
      [e.target.name]: e.target.value,
    })
  _onCustomFieldChange = e =>
    this.setState({
      custom_data: {
        ...this.state.custom_data,
        [e.target.name]: e.target.value,
      },
    })
  _onProSelect = pros => {
    const proValues = pros.map(p => String(p.value).trim())
    this.setState({
      pros: proValues,
    })
  }
  _onPublisherSelect = publishers => {
    const publisherValues = publishers.map(p => String(p.value).trim())
    this.setState({
      publishers: publisherValues,
    })
  }
  _onTagsSelect = (selectedTagOptions: DropdownOption[]) => {
    const tags = selectedTagOptions
      .map(t => `${t.value}`)
      .filter(t => t.trim().toLowerCase() !== 'business')
    this.setState({
      tags,
    })
  }

  render() {
    const {
      proDropdownOptions,
      publisherDropdownOptions,
      saving,
      customFieldRows,
    } = this.props
    const { tags } = this.state
    const showExtraFields = areTagsIncludeValues(
      tags,
      ['Writer', 'Artist'],
      'any',
    )
    const showIdentifierFields = this._showIdentifierFields(tags)
    const nameError = this.props.getValidationMessages('name')[0]
    const emailError =
      this.props.getValidationMessages('email')[0] ||
      this.state.optionalEmailError
    const consentError = this.props.getValidationMessages('consent')[0]
    return (
      <DiscoModal
        onRequestClose={this._onClose}
        isOpen
        style={doubleScrollModalStyles}
      >
        <div className="Form Form_Channel">
          <Heading size="600">Add new contact</Heading>
          <div className="form-note form-note--spaced">
            Need to add more than one contact at once?&nbsp;
            <Link to="/contacts/list/file-import">Import a contact list</Link>
          </div>
          <div className="row">
            <div className="col-xs-12 col-sm-6">
              <TextField
                label="Name"
                name="name"
                value={this.state.name}
                onChange={this._onFieldChange}
                error={nameError}
                required
              />
            </div>
            <div className="col-xs-12 col-sm-6">
              <TextField
                label="Email"
                name="email"
                type="email"
                value={this.state.email}
                onChange={this._onFieldChange}
                error={emailError}
                required={
                  !areTagsIncludeValues(tags, optionalEmailTypes, 'any')
                }
              />
            </div>
          </div>
          <div className="row">
            <div className="col-xs-12 col-sm-6">
              <TextField
                label="Company"
                name="company"
                value={this.state.company}
                onChange={this._onFieldChange}
              />
            </div>
            <div className="col-xs-12 col-sm-6">
              <TextField
                label="Phone"
                name="phone"
                value={this.state.phone}
                onChange={this._onFieldChange}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-xs-12 col-sm-6">
              <TextField
                label="Location"
                name="territory"
                value={this.state.territory}
                onChange={this._onFieldChange}
              />
            </div>
            <div className="col-xs-12 col-sm-6">
              <Label caption="Tags" />
              <TagSelector
                selectedTags={this.state.tags}
                placeholder="Add tags"
                clearable
                allowCreate
                tagsType="contact"
                disabledTags={disabledTags}
                onChange={this._onTagsSelect}
              />
            </div>
          </div>
          {showExtraFields && (
            <div className="row">
              <div className="col-xs-12">
                <Label caption="Publisher" />
                <CustomDropdown
                  name="publisher"
                  placeholder="Select publishers..."
                  promptTextCreator={publisherPromptTextCreator}
                  multi
                  onChange={this._onPublisherSelect}
                  value={this.state.publishers}
                  options={publisherDropdownOptions}
                  allowCreate
                />
              </div>
              <div className="col-xs-12">
                <TextField
                  label="Acts"
                  name="acts"
                  value={this.state.acts}
                  onChange={this._onFieldChange}
                />
              </div>
            </div>
          )}
          {showExtraFields && (
            <div className="row">
              <div className="col-xs-12">
                <Label caption="PRO" />
                <CustomDropdown
                  name="pro"
                  placeholder="Select PRO..."
                  promptTextCreator={proPromptTextCreator}
                  multi
                  onChange={this._onProSelect}
                  value={this.state.pros}
                  options={proDropdownOptions}
                  allowCreate
                />
              </div>
            </div>
          )}
          {showIdentifierFields && (
            <div className="row">
              <div className="col-xs-12 col-sm-6">
                <IdentifierTextField
                  identifierType="ipi"
                  label="IPI"
                  name="ipi"
                  value={this.state.ipi}
                  error={this.state.ipiError}
                  onChange={value =>
                    this.setState({ ipi: value, ipiError: null })
                  }
                />
              </div>
              <div className="col-xs-12 col-sm-6">
                <IdentifierTextField
                  identifierType="isni"
                  label="ISNI"
                  name="isni"
                  value={this.state.isni}
                  error={this.state.isniError}
                  onChange={value =>
                    this.setState({ isni: value, isniError: null })
                  }
                />
              </div>
            </div>
          )}
          {customFieldRows.map((row, index) => {
            return (
              <div className="row" key={`row-${index}`}>
                {row.map(field => (
                  <div className="col-xs-12 col-sm-6" key={field.name}>
                    <TextField
                      label={field.name}
                      name={field.name}
                      value={this.state.custom_data[field.name] || ''}
                      onChange={this._onCustomFieldChange}
                    />
                  </div>
                ))}
              </div>
            )
          })}
          {consentError && (
            <div className="ContactAdd_Error">{consentError}</div>
          )}
          {this.consentCheckboxVisible() && (
            <Checkbox
              label="This person has given me permission to email them."
              checked={this.state.consent}
              onChange={this._handleConsent}
            />
          )}
          <div className="Form_ButtonsContainer separated-small">
            <ButtonContainer>
              <Button caption="Cancel" onClick={this._onClose} />
              <Button
                appearance="primary"
                caption="Add contact"
                progressIndicator={saving}
                progressCaption="Adding contact"
                onClick={this._onSubmit}
              />
            </ButtonContainer>
          </div>
        </div>
      </DiscoModal>
    )
  }
}

const ContactAddWithValidation = validation(strategy)(ContactAdd)

const ContactAddWrapper: React.FC = () => {
  const params = useParams<{ listId: string }>()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { t } = useTranslation('rights')
  const proDropdownOptions = useSelector(getProDropdownOptions)
  const publisherDropdownOptions = useSelector(getPublisherDropdownOptions)
  const saving = useSelector(getContactsSavingStatus)
  const customFieldRows = useSelector(state =>
    chunk(getContactCustomFields(state), 2),
  )

  return (
    <ContactAddWithValidation
      params={params}
      navigate={navigate}
      addContact={(contact: Contact, listId?: number) =>
        dispatch(addContact(contact, listId) as any)
      }
      proDropdownOptions={proDropdownOptions}
      publisherDropdownOptions={publisherDropdownOptions}
      saving={saving}
      customFieldRows={customFieldRows}
      t={t}
    />
  )
}

export default ContactAddWrapper
